'use client';

// biome-ignore lint/style/noRestrictedImports: Used to create our custom Link component
import { default as NextLink } from 'next/link';
import { useContext } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { trackContentCardClick } from '@/app_lib/services/contentCardTracking';
import { type TrackEventParams, trackEvent } from '@/app_lib/services/Gtm/utils';
import type { TrackContentCardClickParams } from '@/app_lib/types/ContentCardTracking';
import { fixedForwardRef } from '@/app_lib/utils/fixedForwardRefUtil';
import { cn } from '@/app_lib/utils/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const contentLink = tv({
  variants: {
    variant: {
      primary:
        'text-blue-500 dark:text-blue-300 hover:text-primary-hover active:text-primary-pressed visited:text-primary-visited',
      secondary: 'text-red-500 dark:text-red-400 hover:underline',
    },
  },
});

const navLink = tv({
  base: 'font-medium',
  variants: {
    variant: {
      primary: 'hover:text-secondary active:text-secondary',
      secondary: 'hover:underline active:underline active:text-secondary',
    },
  },
});

type ContentLinkVariants = VariantProps<typeof contentLink>;
type NavLinkVariants = VariantProps<typeof navLink>;
type LinkVariants = ContentLinkVariants & NavLinkVariants;

export interface LinkOwnProps<E extends 'a' | typeof NextLink = typeof NextLink> extends LinkVariants {
  href: string;
  className?: string;
  prefetch?: boolean;
  children: React.ReactNode;
  trackingData?: TrackEventParams;
  contentCardTrackingData?: TrackContentCardClickParams;
  recipe?: 'content' | 'nav';
  optimizelyTrackingData?: { name: string; properties?: Record<string, string | number> };
  as?: E;
}

export type LinkProps<E extends 'a' | typeof NextLink> = LinkOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof LinkOwnProps<E>>;

/**
 * Use this for all navigation. Polymorphic component for NextLink or anchor tags depending on the `as` prop.
 *
 * @param recipe - the styling group to be used (defaults to no styling)
 */
export const Link = fixedForwardRef(
  <E extends 'a' | typeof NextLink = typeof NextLink>(
    {
      href,
      className,
      prefetch = true,
      children,
      variant,
      onClick,
      trackingData,
      recipe,
      'aria-label': ariaLabel,
      optimizelyTrackingData = { name: '', properties: {} },
      contentCardTrackingData,
      as,
      ...rest
    }: LinkProps<E>,
    forwardedRef: React.Ref<HTMLAnchorElement>,
  ) => {
    const style = recipe === 'content' ? contentLink : navLink;
    const { pageData } = useContext(GtmContext);
    const { optimizely } = useOptimizely();
    const Element = (as ?? NextLink) as React.ElementType;

    return (
      <Element
        aria-label={ariaLabel || `Go to ${href}`}
        className={recipe ? cn(style({ variant }), className) : className}
        href={/%[0-9A-Fa-f]{2}/.test(href) ? href : encodeURI(href)}
        onClick={(e) => {
          if (trackingData) {
            trackEvent({ ...pageData, ...trackingData.data }, trackingData.eventType);
          }
          if (contentCardTrackingData) {
            trackContentCardClick(contentCardTrackingData);
          }
          onClick?.(e);
          if (optimizelyTrackingData?.name) {
            trackOptimizelyEvent(optimizely, optimizelyTrackingData.name, optimizelyTrackingData.properties ?? {});
          }
        }}
        {...(as !== 'a' && { prefetch })}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </Element>
    );
  },
);
